import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { msgs } from "../../msgs";
import { CreatePromotionInput, GeolocationDescriptor } from "../../types";
import { ApprovalCard, CardHeader, MetaLabel, Section, StatePill } from "./shared";
import {
  describeInventoryScope,
  formatRate,
  giveNightMessage,
  htmlToPlainText,
  isGiveNightInput,
  resolveDiscountScope,
} from "./formatting";
import { useJoinAnd, useLocaleName } from "./locale-hooks";
import {
  DeviceTypesValue,
  GeolocationValue,
  WindowRangesView,
  restrictionSentence,
} from "./restriction-views";

export function PromotionApprovalSummary({ input }: { input: unknown }) {
  const intl = useIntl();
  const localeName = useLocaleName();
  const joinAnd = useJoinAnd();
  const data = (input ?? {}) as CreatePromotionInput;
  // GIFT/GIVE-NIGHT (3x2-style) promotions have no user-chosen rate — the free night IS the
  // discount — so the badge and "Descuento y alcance" section render an entirely different
  // sentence for them (see giveNightMessage), built from these same tool-input fields instead of
  // rateType/percentageValue/amount.
  const isGiveNight = isGiveNightInput(data);
  const giveNightArgs = {
    discountedUnits: data.giveNightDiscountedUnits,
    everyUnits: data.giveNightEveryUnits,
  };
  // FEATURED (message/banner-style, forced 0% rate) promotion — see `isFeatured`'s doc comment in
  // types.ts. Unlike give-night, a featured promotion MAY still carry a real inventory scope, so
  // this alone doesn't decide the rendering the way `isGiveNight` does — `featuredHasScope` below
  // (computed once `hasScopedLines` exists) picks between the two featured sentences.
  const isFeatured = data.isFeatured === true;
  // This input is the model-generated tool call echoed for approval, so it can arrive
  // partial or slightly off-shape (e.g. a null entry, or a field that is not the array we
  // expect). Coerce every collection to a real array and drop nullish items so a single
  // malformed value can never throw during render (which, with no error boundary, blanked
  // the whole page when the inventory dropdown was expanded).
  const lines = (Array.isArray(data.appliesToBookable) ? data.appliesToBookable : []).filter(
    l => l != null
  );
  const inventoryByChild = (Array.isArray(data.inventoryByChild)
    ? data.inventoryByChild
    : []
  ).filter(c => c != null);
  // Total resolved lines across all children, used to label the per-child dropdown.
  const perChildLineCount = inventoryByChild.reduce(
    (n, c) => n + (Array.isArray(c.lines) ? c.lines.length : 0),
    0
  );
  const deviceTypes = Array.isArray(data.deviceTypes) ? data.deviceTypes : [];
  const translations = Array.isArray(data.translations) ? data.translations : [];
  const includeCountries = (Array.isArray(data.includeCountries)
    ? data.includeCountries
    : []
  ).filter(c => !!c);
  const excludeCountries = (Array.isArray(data.excludeCountries)
    ? data.excludeCountries
    : []
  ).filter(c => !!c);
  // Unified {mode, countries} descriptor — same shape and rendering (useGeolocationLabel) as the
  // update card's changes.geolocation, so a geolocation restriction reads identically whether it
  // came from creating or updating a promotion. This tool's raw includeCountries/excludeCountries
  // are whatever the model typed (ISO code or free-text name, no server-side resolution before
  // this approval card renders), so `countries` here isn't guaranteed to be ISO codes the way the
  // update flow's is — useGeolocationLabel degrades gracefully either way.
  const geolocationDescriptor: GeolocationDescriptor | null =
    includeCountries.length > 0
      ? { mode: "include", countries: includeCountries }
      : excludeCountries.length > 0
      ? { mode: "exclude", countries: excludeCountries }
      : null;
  // Promo code(s) the backend sends as `codes`; rendered joined so the user can read them.
  const codes = (Array.isArray(data.codes) ? data.codes : []).filter(c => c != null && c !== "");

  // Chain/aggregator creation: the per-child breakdown is present, so the engine list is
  // shown via the inline dropdown below (each engine + its line count) instead of as a
  // separate meta row — showing both would be redundant. Single-property creations never
  // carry `inventoryByChild`, so they keep rendering exactly as before.
  const isChain = inventoryByChild.length > 0;

  // Descriptor for the rates the discount applies to ("tarifas"). We intentionally do NOT
  // enumerate the individual inventory lines — a promotion can match many heterogeneous
  // variants (free cancellation, doubles, non-refundable, occupancy combos, …) and listing
  // them is noisy. Show "todas las tarifas" when it targets everything, otherwise a generic
  // "las tarifas seleccionadas"; the exact count still appears in the subtitle below.
  //
  // NON-chain: delegates to `resolveDiscountScope` (the SAME helper the update card uses) so
  // the count comes from `changes.inventoryScope` — the REAL resolved match count returned by
  // `previewPromotionCreate` — instead of `lines.length` (the number of SELECTOR OBJECTS in
  // `appliesToBookable`, which can each resolve to MANY real lines: this used to make the card
  // show e.g. "Applies to 1 inventory line" for a single condition selector that actually
  // matched dozens of lines). `appliesToBookable` is deliberately NOT passed to the fallback —
  // it holds STRUCTURED selector objects here, not ticker strings, so joining it as a fallback
  // scope value would render garbage; omitting it just means the (rare) case of a stored
  // message from before `changes.inventoryScope` existed shows no count rather than a wrong one.
  //
  // Chain: keeps the pre-existing behaviour untouched — `inventoryByChild` already carries the
  // REAL per-child resolved lines from `previewBulkChildPromotionInventory`, rendered via the
  // dropdown below, so it never had this bug.
  const nonChainScope = !isChain
    ? resolveDiscountScope(
        { changes: data.changes, appliesToAllInventory: data.appliesToAllInventory },
        intl
      )
    : null;
  const hasScopedLines = lines.length > 0 || inventoryByChild.length > 0;
  // Whether THIS featured promotion actually requested an inventory scope at all — `isFeatured`
  // is the one typology that can legitimately have NONE (the common sitewide banner/message
  // case). `hasScopedLines` alone misses the "all inventory" case (appliesToAllInventory:true
  // resolves to no selector objects and no per-child breakdown), hence the extra check.
  const featuredHasScope = Boolean(data.appliesToAllInventory) || hasScopedLines;
  // Specific legend ("Habitaciones Dobles", "Habitaciones no reembolsables"...) built from the
  // server-computed, deterministic `inventoryScopeDescriptor` (see its own doc comment in
  // types.ts) — falls back to the generic "las tarifas seleccionadas" when absent (older persisted
  // messages, "all inventory", or nothing specific to describe). Populated for BOTH the single-
  // property and chain create flows alike (previewChainPromotion derives one overall descriptor
  // from the union of every targeted child's real matched lines).
  const specificScope = describeInventoryScope(data.inventoryScopeDescriptor, intl);
  const rateScope: string | null = isChain
    ? data.appliesToAllInventory
      ? intl.formatMessage(msgs.approvalAllInventory)
      : hasScopedLines
      ? specificScope ?? intl.formatMessage(msgs.approvalSelectedRates)
      : null
    : specificScope ?? nonChainScope!.scopeLabel;

  // Booking AND travel (stay) windows, plus their blackout counterparts, are ALL shown so the
  // card is congruent regardless of which kind of dates the promotion carries. Each is an array
  // of `{ startDate, endDate, weekDays? }` ranges (createPromotionObjectSchema's real shape —
  // NOT flat start/end scalars), so a promotion can carry several disjoint ranges per window.
  const bookingWindows = Array.isArray(data.openToBookingWindows) ? data.openToBookingWindows : [];
  const travelWindows = Array.isArray(data.openToTravelWindows) ? data.openToTravelWindows : [];
  const closeBookingWindows = Array.isArray(data.closeToBookingWindows)
    ? data.closeToBookingWindows
    : [];
  const closeTravelWindows = Array.isArray(data.closeToTravelWindows)
    ? data.closeToTravelWindows
    : [];
  // Unified "Restricciones" section (min/max stay + min/max advance notice) — same rendering as
  // the update card's own section (see restrictionSentence), so a restriction reads identically
  // whether it came from creating or updating a promotion. Replaces the old ad-hoc "Estancia"
  // meta field, which only ever showed min/max STAY (as a bare "≥2 / ≤10", no unit) and never
  // showed minNotice/maxNotice at all — the bug this fixes.
  const hasRestrictions =
    data.minStay != null ||
    data.maxStay != null ||
    data.minNotice != null ||
    data.maxNotice != null;

  // Secondary meta that has no dedicated section in the design but still carries useful
  // data — rendered as a compact two-column grid below the dates.
  const metaFields = [
    data.childPropertyTicker && {
      key: "engine",
      label: intl.formatMessage(msgs.approvalTargetEngine),
      value: data.childPropertyTicker,
    },
    !isChain &&
      data.includedChildTickers?.length && {
        key: "engines",
        label: intl.formatMessage(msgs.approvalTargetEngines),
        value: data.includedChildTickers.join(", "),
      },
    !isChain &&
      data.excludedChildTickers?.length && {
        key: "excl",
        label: intl.formatMessage(msgs.approvalExcludedEngines),
        value: data.excludedChildTickers.join(", "),
      },
    codes.length > 0 && {
      key: "code",
      label: intl.formatMessage(msgs.approvalPromoCode),
      value: codes.join(", "),
    },
    data.group && {
      key: "group",
      label: intl.formatMessage(msgs.approvalGroup),
      // The backend sends the group as "GROUP_<n>"; show only the number, as elsewhere in the app.
      value: data.group.replace(/^GROUP_/, ""),
    },
    // `countries` is NOT in this array: GeolocationValue can render a collapsible <details> for
    // long lists (see GEOLOCATION_COLLAPSE_THRESHOLD), which doesn't fit the plain single-line
    // truncated string this grid renders every other field as. Shown as its own block below.
  ].filter(Boolean) as { key: string; label: string; value: string }[];

  // Which translation to expand first: the one matching the UI's current language,
  // falling back to English, falling back to whichever came first. The set of locales a
  // promotion carries is dynamic (it mirrors whatever the engine has active), so this
  // can't just assume translations[0] is the "main" one.
  const currentLocale = intl.locale.toLowerCase().slice(0, 2);
  const primaryTranslation =
    translations.find(t => t.locale?.toLowerCase().slice(0, 2) === currentLocale) ??
    translations.find(t => t.locale?.toLowerCase().slice(0, 2) === "en") ??
    translations[0];
  const otherTranslations = translations.filter(t => t !== primaryTranslation);

  return (
    <ApprovalCard>
      <CardHeader
        title={data.ticker ?? "—"}
        badge={
          isGiveNight
            ? intl.formatMessage(msgs.approvalGiveNightBadge, giveNightArgs)
            : isFeatured
            ? intl.formatMessage(msgs.approvalFeaturedBadge)
            : intl.formatMessage(msgs.approvalRateBadge, { rate: formatRate(data, intl) })
        }
      />

      {/* Estado al crearse — promotions are always created inactive and not visible. */}
      <Section label={intl.formatMessage(msgs.approvalSectionStatus)}>
        <div className="flex flex-wrap gap-2">
          <StatePill icon="fa-minus-circle">
            {intl.formatMessage(msgs.approvalStateInactive)}
          </StatePill>
          <StatePill icon="fa-eye-slash">
            {intl.formatMessage(msgs.approvalStateNotVisible)}
          </StatePill>
        </div>
      </Section>

      {/* Descuento y alcance — always the locally-derived rate/scope sentence built from the
          structured input fields, followed by the applied inventory-line count. No longer
          reads the backend's confirmationSummary text (removed — see below). */}
      <Section label={intl.formatMessage(msgs.approvalSectionDiscount)}>
        {isGiveNight ? (
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(giveNightMessage(data.giveNightCriteria), {
              ...giveNightArgs,
              b: (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>,
            })}
          </div>
        ) : isFeatured ? (
          // No rate to show (forced 0%, never model-supplied) — the sentence names the SCOPE
          // instead, same as `rateScope` would for a normal promotion, but falls back to a
          // dedicated "sitewide, no inventory" sentence when no scope was requested at all
          // (rateScope/nonChainScope can't be trusted for that case — see their own comments).
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(
              featuredHasScope ? msgs.approvalFeaturedWithScope : msgs.approvalFeaturedSitewide,
              {
                scope: rateScope ?? intl.formatMessage(msgs.approvalSelectedRates),
                b: (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>,
              }
            )}
          </div>
        ) : rateScope ? (
          <div className="text-[#212121] text-sm">
            {intl.formatMessage(msgs.approvalDiscountScope, {
              rate: formatRate(data, intl),
              scope: rateScope,
              b: (chunks: ReactNode) => <strong className="font-bold">{chunks}</strong>,
            })}
          </div>
        ) : (
          <div className="text-[#212121] text-sm">
            <strong className="font-bold">{formatRate(data, intl)}</strong>
          </div>
        )}
        {isChain
          ? perChildLineCount > 0 && (
              // Chain: collapsed-by-default dropdown replacing the plain count — rendered as a
              // bordered control (matches the design's select-like affordance) that expands
              // in place to a scrollable per-child breakdown (engine ticker, its line count,
              // and any requested-but-missing lines) — the same info the old bottom section
              // used to show permanently.
              <details className="group w-full rounded-[10px] border border-[#E5E7EB] bg-white overflow-hidden">
                <summary className="flex h-[43px] items-center justify-between gap-2 px-4 cursor-pointer list-none text-[#212121] text-sm marker:content-none [&::-webkit-details-marker]:hidden">
                  {intl.formatMessage(msgs.approvalInventoryLines, { count: perChildLineCount })}
                  <em className="fa fa-chevron-down text-[13px] text-[#212121]" aria-hidden />
                </summary>
                <div className="flex flex-col gap-2 max-h-[168px] overflow-y-auto px-4 pb-3">
                  {inventoryByChild.map((child, ci) => {
                    const childLines = (Array.isArray(child.lines) ? child.lines : []).filter(
                      l => l != null
                    );
                    const missing = (Array.isArray(child.missingLines)
                      ? child.missingLines
                      : []
                    ).filter(m => m != null);
                    return (
                      <div key={child.ticker ?? ci} className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-[#212121] text-sm break-all">
                          {intl.formatMessage(msgs.approvalInventoryChildLines, {
                            ticker: child.ticker ?? "—",
                            count: childLines.length,
                          })}
                        </span>
                        {missing.length > 0 && (
                          <span className="text-[#B45309] text-sm pl-2 break-words">
                            {intl.formatMessage(msgs.approvalInventoryMissing, {
                              lines: missing.join(", "),
                            })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            )
          : // Single-property create: the REAL resolved count/caption from `changes.inventoryScope`
            // (via `resolveDiscountScope`, same helper the update card uses) — see this component's
            // own comment on `nonChainScope` above for why this is no longer a bare selector count.
            nonChainScope?.lineCountCaption && (
              <span className="block min-w-[220px] min-h-[19px] text-[#949494] text-[14px] font-normal leading-none">
                {nonChainScope.lineCountCaption}
              </span>
            )}
      </Section>

      {/* Repetible — GIVE_NIGHT only: whether the discount cycle repeats within the same stay. */}
      {isGiveNight && (
        <Section label={intl.formatMessage(msgs.approvalGiveNightRepeatable)}>
          <span className="text-[#212121] text-sm">
            {intl.formatMessage(data.giveNightRepeatable ? msgs.confirmYes : msgs.confirmNo)}
          </span>
        </Section>
      )}

      {/* Restricciones — min/max stay AND min/max advance notice unified under one section, each
          rendered as its own bolded "<label> de <value>" sentence, same as the update card. */}
      {hasRestrictions && (
        <Section label={intl.formatMessage(msgs.approvalRestrictionsTitle)}>
          <div className="flex flex-col gap-1.5">
            {data.minStay != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMinStay, data.minStay, "nights")}
              </span>
            )}
            {data.maxStay != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMaxStay, data.maxStay, "nights")}
              </span>
            )}
            {data.minNotice != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMinNotice, data.minNotice, "days")}
              </span>
            )}
            {data.maxNotice != null && (
              <span className="text-[#212121] text-sm break-words">
                {restrictionSentence(intl, msgs.approvalMaxNotice, data.maxNotice, "days")}
              </span>
            )}
          </div>
        </Section>
      )}

      {travelWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalStayDates)}>
          <WindowRangesView windows={travelWindows} />
        </Section>
      )}
      {bookingWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalBookingDates)}>
          <WindowRangesView windows={bookingWindows} />
        </Section>
      )}
      {closeTravelWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalCloseTravelWindow)}>
          <WindowRangesView windows={closeTravelWindows} />
        </Section>
      )}
      {closeBookingWindows.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalCloseBookingWindow)}>
          <WindowRangesView windows={closeBookingWindows} />
        </Section>
      )}

      {deviceTypes.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalDeviceTypes)}>
          <DeviceTypesValue devices={deviceTypes} intl={intl} />
        </Section>
      )}

      {metaFields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 pl-3 pr-4 py-3 bg-white">
          {metaFields.map(({ key, label, value }) => (
            <div key={key} className="flex flex-col gap-0.5 min-w-0">
              <MetaLabel>{label}</MetaLabel>
              <span className="text-[#212121] text-sm truncate">{value}</span>
            </div>
          ))}
        </div>
      )}

      {geolocationDescriptor && (
        <div className="flex flex-col gap-1 min-w-0 pl-3 pr-4 py-3 bg-white">
          <MetaLabel>{intl.formatMessage(msgs.approvalCountries)}</MetaLabel>
          <GeolocationValue descriptor={geolocationDescriptor} />
        </div>
      )}

      {/* Traducciones — the primary language expanded, the rest summarised by name. */}
      {translations.length > 0 && (
        <Section label={intl.formatMessage(msgs.approvalTranslations)}>
          <div className="flex flex-col gap-1">
            <span className="font-bold text-[#212121] text-sm">
              {localeName(primaryTranslation.locale)}
            </span>
            {primaryTranslation.name && (
              <span className="text-[#212121] text-sm break-words">
                <span className="font-semibold">{htmlToPlainText(primaryTranslation.name)}</span>
                {primaryTranslation.description
                  ? `: ${htmlToPlainText(primaryTranslation.description)}`
                  : ""}
              </span>
            )}
          </div>
          {otherTranslations.length > 0 && (
            <span className="font-bold text-[#212121] text-sm">
              {joinAnd(otherTranslations.map(t => localeName(t.locale)))}
            </span>
          )}
        </Section>
      )}
    </ApprovalCard>
  );
}
